import { useMutation, useQueryClient, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useRef } from "react";

import { IdentitiesPage } from "../../../components/dashboard/identities";
import { useOneTimePageAnimation } from "@/components/motion";
import {
  applyRegistrarDomainSetupAction,
  disconnectRegistrarSessionAction,
  startRegistrarSessionAction,
} from "@/features/domain-automation/server-fns";
import {
  registrarApplyStatusQueryOptions,
  registrarCapabilitiesQueryOptions,
  registrarPlanQueryOptions,
} from "@/features/domain-automation/queries";
import type { StartRegistrarSessionInput } from "@/features/domain-automation/shared";
import {
  getIdentitiesView,
  recheckDomainVerification,
  removeAlias,
  removeDomain,
  saveAlias,
  updateDomainInbound,
} from "@/features/identities/server-fns";
import {
  domainAliasesQueryKey,
  domainAliasesQueryOptions,
  domainDnsQueryOptions,
  identitiesQueryKey,
  identitiesQueryOptions,
} from "@/features/identities/queries";
import type { IdentityAliasView } from "@/features/identities/view-model";
import { mapAliasToView } from "@/features/identities/view-model";
import { capturePostHogEvent } from "@/lib/posthog-client";

export const Route = createFileRoute("/dashboard/identities/")({
  validateSearch: (search: Record<string, unknown>) => ({
    domainId: (search.domainId as string) || undefined,
    setup: (search.setup as string) || undefined,
  }),
  head: () => ({
    meta: [{ title: "Identities - Bavimail" }],
  }),
  staleTime: 30_000,
  preloadStaleTime: 30_000,
  loader: async ({ context }) => {
    await context.queryClient.ensureQueryData(identitiesQueryOptions());
  },
  component: IdentitiesRoute,
});

function IdentitiesRoute() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { domainId } = Route.useSearch();
  const { data } = useSuspenseQuery({
    ...identitiesQueryOptions(),
    refetchInterval: (query) => {
      const current = query.state.data;
      if (!current) return false;

      return current.domains.some(
        (domain) =>
          domain.status === "provisioning" ||
          domain.status === "pending" ||
          domain.status === "verifying",
      )
        ? 5_000
        : false;
    },
  });
  const didTrackVerifiedDomainsRef = useRef(false);
  const previousVerifiedDomainIdsRef = useRef<Set<string>>(new Set());
  const domainAnimationStage = useOneTimePageAnimation("dashboard-identities-domains");
  const effectiveSelectedDomainId =
    domainId && data.domains.some((domain) => domain.id === domainId)
      ? domainId
      : data.domains[0]?.id;
  const selectedDomain = data.domains.find((domain) => domain.id === effectiveSelectedDomainId);
  const selectedDomainAliasesKey =
    selectedDomain?.status === "verified" ? domainAliasesQueryKey(selectedDomain.id) : null;
  const selectedDomainAliasesLoaded = selectedDomainAliasesKey
    ? Boolean(queryClient.getQueryState(selectedDomainAliasesKey)?.dataUpdatedAt)
    : false;
  const initialAliases = useMemo(() => {
    if (!selectedDomain || selectedDomain.status !== "verified") {
      return [];
    }

    return (
      queryClient.getQueryData<IdentityAliasView[]>(domainAliasesQueryKey(selectedDomain.id)) ?? []
    );
  }, [queryClient, selectedDomain]);

  useEffect(() => {
    const currentVerifiedDomainIds = new Set(
      data.domains.filter((domain) => domain.status === "verified").map((domain) => domain.id),
    );

    if (!didTrackVerifiedDomainsRef.current) {
      previousVerifiedDomainIdsRef.current = currentVerifiedDomainIds;
      didTrackVerifiedDomainsRef.current = true;
      return;
    }

    const hasNewVerifiedDomain = [...currentVerifiedDomainIds].some(
      (verifiedDomainId) => !previousVerifiedDomainIdsRef.current.has(verifiedDomainId),
    );

    previousVerifiedDomainIdsRef.current = currentVerifiedDomainIds;

    if (!hasNewVerifiedDomain) {
      return;
    }

    void Promise.all([
      queryClient.invalidateQueries({ queryKey: ["composeOptions"] }),
      queryClient.invalidateQueries({ queryKey: ["verifiedIdentityOptions"] }),
    ]);
  }, [data.domains, queryClient]);

  const verifyMutation = useMutation({
    mutationFn: recheckDomainVerification,
    onSuccess: (result) => {
      if (result.domain.status === "verified") {
        capturePostHogEvent("domain_verified", { domain: result.domain.domain });
      }
      queryClient.setQueryData<Awaited<ReturnType<typeof getIdentitiesView>>>(
        identitiesQueryKey(),
        (current) => {
          if (!current) return current;
          return {
            ...current,
            domains: current.domains.map((domain) => {
              if (domain.id !== result.domain.id) return domain;
              // Status is owned by the worker and surfaced via the polling list query.
              // A point-in-time re-read must never downgrade an already-verified domain
              // back to the setup screen, so keep `verified` and only refresh DNS progress.
              if (domain.status === "verified" && result.domain.status !== "verified") {
                return { ...domain, dnsProgress: result.domain.dnsProgress };
              }
              return result.domain;
            }),
          };
        },
      );
      queryClient.setQueryData(domainDnsQueryOptions(result.domain.id).queryKey, result.dns);
      if (result.domain.status === "verified") {
        void Promise.all([
          queryClient.invalidateQueries({ queryKey: ["composeOptions"] }),
          queryClient.invalidateQueries({ queryKey: ["verifiedIdentityOptions"] }),
        ]);
      }
    },
  });

  const toggleInboundMutation = useMutation({
    mutationFn: updateDomainInbound,
    onMutate: async ({ data: mutationData }) => {
      await queryClient.cancelQueries({ queryKey: identitiesQueryKey() });
      const previous =
        queryClient.getQueryData<Awaited<ReturnType<typeof getIdentitiesView>>>(
          identitiesQueryKey(),
        );
      queryClient.setQueryData<Awaited<ReturnType<typeof getIdentitiesView>>>(
        identitiesQueryKey(),
        (current) => {
          if (!current) return current;
          return {
            ...current,
            domains: current.domains.map((domain) =>
              domain.id === mutationData.domainId
                ? { ...domain, inboundEnabled: mutationData.inboundEnabled }
                : domain,
            ),
          };
        },
      );
      return { previous };
    },
    onSuccess: (result) => {
      queryClient.setQueryData<Awaited<ReturnType<typeof getIdentitiesView>>>(
        identitiesQueryKey(),
        (current) => {
          if (!current) return current;
          return {
            ...current,
            domains: current.domains.map((domain) =>
              domain.id === result.domain.id ? result.domain : domain,
            ),
          };
        },
      );
      queryClient.setQueryData(domainDnsQueryOptions(result.domain.id, true).queryKey, result.dns);
      void queryClient.invalidateQueries({ queryKey: ["domainDns", result.domain.id] });
    },
    onError: (_error, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(identitiesQueryKey(), context.previous);
      }
    },
  });

  const deleteDomainMutation = useMutation({
    mutationFn: removeDomain,
    onMutate: async ({ data: mutationData }) => {
      await queryClient.cancelQueries({ queryKey: identitiesQueryKey() });
      const previous =
        queryClient.getQueryData<Awaited<ReturnType<typeof getIdentitiesView>>>(
          identitiesQueryKey(),
        );
      queryClient.setQueryData<Awaited<ReturnType<typeof getIdentitiesView>>>(
        identitiesQueryKey(),
        (current) => {
          if (!current) return current;
          return {
            domains: current.domains.filter((domain) => domain.id !== mutationData.domainId),
          };
        },
      );
      return { previous };
    },
    onError: (_error, _variables, context) => {
      if (context?.previous) {
        queryClient.setQueryData(identitiesQueryKey(), context.previous);
      }
    },
  });

  const updateAliasMutation = useMutation({
    mutationFn: saveAlias,
    onSuccess: (updated) => {
      const aliasView = mapAliasToView(updated);
      queryClient.setQueryData<IdentityAliasView[]>(
        domainAliasesQueryKey(aliasView.domainId),
        (current) =>
          current?.map((alias) => (alias.id === aliasView.id ? aliasView : alias)) ?? current,
      );
    },
  });

  const deleteAliasMutation = useMutation({
    mutationFn: removeAlias,
    onMutate: async ({ data: mutationData }) => {
      const previousAliases = queryClient.getQueriesData({ queryKey: ["domainAliases"] });
      queryClient.setQueriesData(
        { queryKey: ["domainAliases"] },
        (current: Array<{ id: string }> | undefined) =>
          current?.filter((alias) => alias.id !== mutationData.aliasId) ?? current,
      );
      return { previousAliases };
    },
    onError: (_error, _variables, context) => {
      for (const [queryKey, previous] of context?.previousAliases || []) {
        queryClient.setQueryData(queryKey as readonly unknown[], previous);
      }
    },
  });

  async function prefetchDomain(domainIdToPrefetch: string) {
    const domain = data.domains.find((item) => item.id === domainIdToPrefetch);
    if (!domain) return;

    if (domain.status === "verified") {
      await queryClient.prefetchQuery(domainAliasesQueryOptions(domainIdToPrefetch));
      return;
    }

    await queryClient.prefetchQuery(domainDnsQueryOptions(domainIdToPrefetch));
  }

  return (
    <IdentitiesPage
      initialDomains={data.domains}
      initialAliases={initialAliases}
      initialAliasesLoaded={selectedDomainAliasesLoaded}
      initialSelectedDomainId={effectiveSelectedDomainId}
      domainAnimationStage={domainAnimationStage}
      onSelectDomain={(selectedDomainId) => {
        void navigate({
          to: "/dashboard/identities",
          search: { domainId: selectedDomainId, setup: undefined },
        });
      }}
      onPrefetchDomain={(selectedDomainId) => {
        void prefetchDomain(selectedDomainId);
      }}
      loadDomainAliases={async (selectedDomainId: string) =>
        await queryClient.fetchQuery(domainAliasesQueryOptions(selectedDomainId))
      }
      loadDomainDns={async (selectedDomainId, forceRefresh) => {
        if (forceRefresh) {
          await queryClient.invalidateQueries({ queryKey: ["domainDns", selectedDomainId] });
        }
        return await queryClient.fetchQuery(domainDnsQueryOptions(selectedDomainId, forceRefresh));
      }}
      onVerifyDomain={async (selectedDomainId) =>
        await verifyMutation.mutateAsync({ data: { domainId: selectedDomainId } })
      }
      onStartRegistrarSession={async (input: StartRegistrarSessionInput) =>
        await startRegistrarSessionAction({ data: input })
      }
      onGetRegistrarCapabilities={async (sessionId) =>
        await queryClient.fetchQuery(
          registrarCapabilitiesQueryOptions(
            selectedDomain?.id || effectiveSelectedDomainId || "active",
            sessionId,
          ),
        )
      }
      onPlanRegistrarSetup={async (sessionId, selectedDomainId) =>
        await queryClient.fetchQuery(registrarPlanQueryOptions(selectedDomainId, sessionId))
      }
      onApplyRegistrarSetup={async (input) =>
        await applyRegistrarDomainSetupAction({ data: input })
      }
      onGetRegistrarApplyStatus={async (operationId) =>
        await queryClient.fetchQuery(registrarApplyStatusQueryOptions(operationId))
      }
      onDisconnectRegistrarSession={async (sessionId) => {
        await disconnectRegistrarSessionAction({ data: { sessionId } });
      }}
      onToggleInbound={async (selectedDomainId, inboundEnabled) => {
        await toggleInboundMutation.mutateAsync({
          data: { domainId: selectedDomainId, inboundEnabled },
        });
      }}
      onDeleteDomain={async (selectedDomainId) => {
        await deleteDomainMutation.mutateAsync({ data: { domainId: selectedDomainId } });
        const remaining =
          queryClient.getQueryData<Awaited<ReturnType<typeof getIdentitiesView>>>(
            identitiesQueryKey(),
          );
        const nextSelectedDomainId = remaining?.domains[0]?.id;
        await navigate({
          to: "/dashboard/identities",
          search: { domainId: nextSelectedDomainId, setup: undefined },
        });
      }}
      onUpdateAlias={async (aliasId, signatureHtml) => {
        const updated = await updateAliasMutation.mutateAsync({
          data: { aliasId, signatureHtml },
        });
        return mapAliasToView(updated);
      }}
      onDeleteAlias={async (aliasId) => {
        await deleteAliasMutation.mutateAsync({ data: { aliasId } });
      }}
    />
  );
}
